import {
  setBindingLoading,
  setValueBindingScreen,
} from '@common/redux/slice/page';
import { IAppInfo, IPage } from '@common/types/element';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getScreenBinding } from '@common/services';
import { FindScreenBindingResult } from 'click-types';
import { isEmpty, findIndex, debounce } from 'lodash';
import { pullToRefreshSelector } from '@common/redux/selectors/page';
import { setDefaultValues } from '@common/redux/slice/formInputs';

type Props = {
  screen: IPage;
  dependencies: Record<string, any>;
  appInfo?: Pick<IAppInfo, 'id'>;
};

const filterDatePicker = (metadata: Record<string, any>[]) =>
  metadata.filter(
    (child) =>
      child.type === 'date-picker' &&
      child?.defaultDateValue?.label === 'Current Time'
  );

const convertDateValue = (
  values: Record<string, any>,
  metadata: Record<string, any>[] = []
) => {
  return Object.keys(values).reduce((acc, id) => {
    const index = findIndex(filterDatePicker(metadata), { id: id });
    return {
      ...acc,
      ...(index === -1 && { [id]: values[id] }),
    };
  }, {});
};

const getDefaultDateValue = (bindingResult: FindScreenBindingResult) => {
  const result: Record<string, string> = {};
  Object.entries(bindingResult?.data).forEach(([id, value]: [string, any]) => {
    if (value?.defaultDateValue) {
      result[id] = value?.defaultDateValue;
    }
  });
  return result;
};

function useBindingScreen({ screen, dependencies, appInfo }: Props): null {
  const dispatch = useDispatch();
  const refreshing = useSelector(pullToRefreshSelector);

  const fetBindingPage = useCallback(
    debounce(async (controller: AbortController) => {
      try {
        if (appInfo) {
          dispatch(setBindingLoading(true));
          const screenBindingResult: FindScreenBindingResult =
            await getScreenBinding({
              appId: appInfo?.id,
              dependencies,
              screenUuid: screen.screenUuid,
              controller,
            });
          if (isEmpty(screenBindingResult?.data)) return;

          dispatch(setDefaultValues(getDefaultDateValue(screenBindingResult)));
          dispatch(
            setValueBindingScreen({
              data: convertDateValue(
                screenBindingResult?.data,
                screen.metadata
              ),
              screenUuid: screen.screenUuid,
            })
          );
        }
      } catch (error) {
        // console.log('error', error);
      }
    }, 300),
    [screen, dependencies, appInfo, refreshing]
  );

  useEffect(() => {
    if (!appInfo) return;
    const controller = new AbortController();
    fetBindingPage(controller);

    return () => {
      controller.abort();
    };
  }, [appInfo, fetBindingPage]);

  return null;
}

export default useBindingScreen;
